import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Row, Col, Typography, Space, Tag, Button, Collapse, Select } from 'antd'
import {
  LinkOutlined,
  BookOutlined,
  PartitionOutlined,
  EditOutlined,
} from '@ant-design/icons'
import ProfileCard from '../ProfileCard'
import RelateFacilityModal from '../Modals/RelateDoctorFacilityModal'
import MedicalEntityModal from '../Modals/MedicalEntityModal'
import ConfirmationModal from '../Modals/ConfirmationModal'
import UpdateFacilityForm from '../Forms/FacilityProfileForm'
import { FACILITY } from '../../../constants'
import { facilityMRDColumns } from '../../../data'
import { getRelatedFacilityProfileFacilities } from '../../../state/modules/facilities'
import Table from '../Tables/SearchTable'

const FacilityProfileMRD = ({
  handleSetMRDFacility,
  unlinkFacilityFromFacilityProfile,
  relateFacilityProfile,
  treatment_facilities,
  mrd_facility,
  relatedFacilityProfilesCount,
  id,
  isUserActionAllowed,
  filterValue,
  onFilterChange,
  facilityData,
  initialValues,
  handleFacilityProfileUpdate,
  createFacilityProfileSchema,
}) => {
  const dispatch = useDispatch()

  const [isConfirmVisible, setIsConfirmVisible] = useState(false)
  const [pendingMrdId, setPendingMrdId] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentSelectedKeys = mrd_facility ? [mrd_facility.id] : []

  useEffect(() => {
    const params = { data: +id }

    if (filterValue === 'Yes') {
      params.special_release = true
    } else if (filterValue === 'No') {
      params.special_release = false
    }

    dispatch(getRelatedFacilityProfileFacilities(params))
  }, [filterValue, id, dispatch])

  const onMrdSelect = selectedId => {
    setPendingMrdId(selectedId)
    setIsConfirmVisible(true)
  }

  const closeModal = () => {
    setIsConfirmVisible(false)
    setPendingMrdId(null)
    setIsSubmitting(false)
  }

  const handleConfirm = async () => {
    if (!pendingMrdId) return

    setIsSubmitting(true)

    try {
      await handleSetMRDFacility(pendingMrdId)

      closeModal()
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {' '}
      {mrd_facility && (
        <Col span={24}>
          <ProfileCard
            title={
              <Space size={16}>
                <div className="profile-title-left-section">
                  Provider
                  <Tag
                    color="processing"
                    icon={<BookOutlined />}
                    style={{ marginLeft: 16 }}
                  >
                    MRD
                  </Tag>
                  <Row>
                    {facilityData?.is_internal && (
                      <Tag color="processing">Handled Internally</Tag>
                    )}
                    {facilityData?.health_networks?.map(item => (
                      <Col style={{ marginBottom: '2px' }} key={item.id}>
                        <Link to={`/health-networks/${item.id}`} target="_blank">
                          <Tag color="processing" icon={<PartitionOutlined />}>
                            {item.name}
                          </Tag>
                        </Link>
                      </Col>
                    ))}
                  </Row>
                </div>
                <Row>
                  {isUserActionAllowed('view_case') && (
                    <Col>
                      {' '}
                      <Link
                        to={{
                          pathname: '/cases',
                          filter: {
                            facility_profile__id: id,
                            status: 4,
                          },
                        }}
                      >
                        <Button className="profile-title-button" type="primary" ghost>
                          View All Cases
                        </Button>
                      </Link>
                    </Col>
                  )}
                  <Col style={{ marginLeft: 10 }}>
                    <MedicalEntityModal
                      title="Edit Facility Profile"
                      trigger={
                        isUserActionAllowed('change_facilityprofile') ? (
                          <Button
                            className="profile-title-button"
                            type="primary"
                            ghost
                            icon={<EditOutlined />}
                          >
                            Edit
                          </Button>
                        ) : (
                          <></>
                        )
                      }
                      form={
                        <UpdateFacilityForm
                          initialValues={initialValues}
                          onSubmit={handleFacilityProfileUpdate}
                          validationSchema={createFacilityProfileSchema}
                          action="edit"
                          entityId={id}
                        />
                      }
                    />
                  </Col>
                </Row>
              </Space>
            }
            style={{
              headerColor: '#E6F7FF',
              bodyColor: '#003A8C',
            }}
          >
            <Table
              columns={facilityMRDColumns}
              totalData={[mrd_facility]}
              entity={FACILITY}
            />
          </ProfileCard>
          <Collapse>
            <Collapse.Panel
              header={
                <>
                  Facilities{' '}
                  {!!relatedFacilityProfilesCount && relatedFacilityProfilesCount}
                </>
              }
              key="1"
            >
              <Table
                tableTitle={
                  <Row justify="end" align="middle">
                    <Col>
                      <Space align="center">
                        <Typography.Text style={{ fontSize: 14 }}>
                          Special Release:
                        </Typography.Text>
                        <Select
                          value={filterValue}
                          onChange={onFilterChange}
                          style={{ width: 80 }}
                          size="small"
                          options={[
                            { value: 'All', label: 'All' },
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <RelateFacilityModal
                        title="Relate Facility"
                        entity="Facility"
                        onSubmit={relateFacilityProfile}
                        closeAfterSubmit
                        trigger={
                          isUserActionAllowed('manage_relations_facilityprofile') ? (
                            <Button type="text" size="large">
                              <Typography.Text type="secondary">
                                <Space>
                                  <LinkOutlined /> Relate Facility
                                </Space>
                              </Typography.Text>
                            </Button>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Col>
                  </Row>
                }
                columns={facilityMRDColumns}
                totalData={treatment_facilities}
                onLoadData={getRelatedFacilityProfileFacilities}
                totalCount={relatedFacilityProfilesCount}
                formData={+id}
                entity={FACILITY}
                hideTotalCount
                customRowSelection={{
                  columnTitle: 'mrd',
                  key: 'is_mrd',
                  onSelect: onMrdSelect,
                  disabled: isUserActionAllowed('update_mrd_facilityprofile'),
                  selectedRowKeys: currentSelectedKeys,
                  selectedId: mrd_facility?.id,
                }}
                rowDeletion={
                  isUserActionAllowed('manage_relations_facilityprofile') && {
                    tooltip: 'Remove Relation',
                    confirmMsg: 'Are you sure you want to remove the relation?',
                    okText: 'Yes',
                    onConfirm: unlinkFacilityFromFacilityProfile,
                    hideRule: item => item.is_mrd,
                  }
                }
              />
            </Collapse.Panel>
          </Collapse>
        </Col>
      )}
      <ConfirmationModal
        visible={isConfirmVisible}
        title="Confirm Update"
        content="Are you sure you want to submit this change?"
        onConfirm={handleConfirm}
        onCancel={closeModal}
        confirmText="Submit"
        cancelText="Cancel"
        isLoading={isSubmitting}
      />
    </>
  )
}

export default FacilityProfileMRD
